<template>
  <div>
    <v-data-table
      v-if="!loadingLikedSections"
      :items="orderedSections"
      v-model:opened="openedRegions"
      :headers="getHeaders()"
      :loading="loadingSection"
      :items-per-page="-1"
      hide-default-footer
      :group-by="[{ key: 'region', order: 'asc' }]"
      :group-key="({ value }) => value"
    >
      <template v-slot:[`header.river`]="{ column }">
        <div class="d-flex align-center justify-space-between">
          <span>{{ column.title }}</span>
          <v-btn size="x-small" variant="text" color="primary" @click.stop="toggleAllRegions">
            {{ allRegionsExpanded ? "Tout réduire" : "Tout déplier" }}
          </v-btn>
        </div>
      </template>
      <!-- On one line in the narrow column; wrapped, its first line went under the filter button -->
      <template v-slot:[`header.current_data`]>
        <span class="flow-header">Débit (<AcronymTooltip code="mcs" />)</span>
      </template>

      <template v-slot:no-data>
        <v-empty-state
          icon="mdi-map-search-outline"
          size="64"
          title="Aucune section trouvée"
          text="Essayez d'élargir votre recherche ou vérifiez votre connexion internet."
        ></v-empty-state>
      </template>

      <template v-slot:group-header="{ item: group, columns, toggleGroup, isGroupOpen }">
        <tr class="region-header" @click="toggleGroup(group)">
          <td :colspan="columns.length" id="region">
            <div class="d-flex align-center ga-1">
              <v-icon size="small" color="primary">{{ isGroupOpen(group) ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
              <span class="region-header__name">{{ group.value }}</span>
              <span class="region-header__count">· {{ group.items.length }} section{{ group.items.length > 1 ? "s" : "" }}</span>
            </div>
          </td>
        </tr>
      </template>

      <template v-slot:[`item`]="{ item }">
        <tr :class="{ 'row--no-data': !hasFlowData(item) }" @click="showRiver(item)">
          <td :class="[item._borderClass, 'section-cell']" valign="top">
            <section-row-info
              :section="item"
              :liked="item.isLiked"
              @toggle-like="toggleLike(item)"
            ></section-row-info>
          </td>
          <td class="pl-0 pr-1 text-left" valign="top">
            <section-flow-summary v-if="isMember || item.free_access === true" :section="item"></section-flow-summary>
            <div v-else>
              <strong id="blur">0.00</strong> <br />
              <span class="premium-hint">
                <v-icon size="x-small">mdi-lock-outline</v-icon>
                Premium
              </span>
              <br />
              <span id="blur">42m</span>
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-skeleton-loader v-else type="table-heading, list-item-three-line@8"></v-skeleton-loader>
  </div>
</template>

<script>
  import AcronymTooltip from "@/components/AcronymTooltip";
  import SectionFlowSummary from "./SectionFlowSummary.vue";
  import SectionRowInfo from "./SectionRowInfo.vue";
  import { sectionHasCamera } from "@/utils/cameras";
  import { levelTextClass } from "@/utils/levelStatus";
  import { RiversService } from "@/services/Rivers.service";
  import { StringService } from "@/services/String.service";
  import { UserService } from "@/store/modules/user";

  export default {
    components: {
      AcronymTooltip,
      SectionFlowSummary,
      SectionRowInfo,
    },
    props: {
      sections: {
        type: Array,
        default() {
          return [];
        },
      },
      loadingSection: {
        type: Boolean,
        default: false,
      },
      isMember: {
        type: Boolean,
        default: false,
      },
      authUser: {
        type: Object,
        default: null,
      },
      defaultRegionsExpanded: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        localSections: [], // Local reactive copy of sections
        displaySectionCache: {},
        loadingLikedSections: true, // Flag to track loading state
        classLevelTile: "Poire",
        classLevelWidth: "",
        classLevelFixed: false,
        minMaxWidth: "",
        minMaxFixed: false,
        headers: [
          { key: "data-table-group", headerProps: { class: "d-none" } },
          {
            title: "Rivière / Section",
            key: "river",
            sortable: false,
            headerProps: { class: "bg-table-header" },
          },
          {
            title: "Débit",
            key: "current_data",
            sortable: false,
            width: "80px",
            headerProps: { class: "bg-table-header text-right pl-0" },
          },
        ],
        headersMobile: [
          { key: "data-table-group", headerProps: { class: "d-none" } },
          {
            title: "Rivière / Section",
            key: "river",
            sortable: false,
            headerProps: { class: "bg-table-header" },
          },
          {
            title: "Débit",
            key: "current_data",
            sortable: false,
            width: "80px",
            headerProps: { class: "bg-table-header text-right pl-0" },
          },
        ],
        level_unknown: "unknown",
        level_low: "low",
        level_recommended: "recommended",
        level_high: "high",
        openedRegions: [],
        hasAppliedDefaultRegionsState: false,
      };
    },
    computed: {
      // Sections with flow data first; within the region groups the table keeps this order
      orderedSections() {
        return [...this.localSections].sort((a, b) => Number(this.hasFlowData(b)) - Number(this.hasFlowData(a)));
      },
      regionNames() {
        return [...new Set(this.localSections.map((section) => section.region))];
      },
      allRegionsExpanded() {
        return this.regionNames.length > 0 && this.regionNames.every((region) => this.openedRegions.includes(region));
      },
    },
    watch: {
      sections: {
        immediate: true,
        handler(newSections) {
          const cache = this.displaySectionCache || {};
          const nextCache = {};

          // Reuse computed display rows when source fields did not change.
          this.localSections = (newSections || []).map((section, index) => {
            const sectionId = section.id || section.sectionId || section.alias || `row-${index}`;
            const signature = this.getDisplaySignature(section, sectionId);
            const cached = cache[sectionId];

            if (cached && cached._displaySignature === signature) {
              nextCache[sectionId] = cached;
              return cached;
            }

            const displaySection = this.toDisplaySection(section, sectionId);
            nextCache[sectionId] = displaySection;
            return displaySection;
          });

          this.displaySectionCache = nextCache;

          // Liked state is already resolved upstream in RiverList.
          this.loadingLikedSections = false;
          this.hasAppliedDefaultRegionsState = false;
          this.applyDefaultRegionsExpandedState();
        },
      },
      defaultRegionsExpanded() {
        this.hasAppliedDefaultRegionsState = false;
        this.applyDefaultRegionsExpandedState();
      },
    },
    methods: {
      hasFlowData(section) {
        return Number(section.current_level) > 0;
      },
      setAllRegionsExpanded(expanded) {
        this.openedRegions = expanded ? [...this.regionNames] : [];
      },
      toggleAllRegions() {
        this.setAllRegionsExpanded(!this.allRegionsExpanded);
      },
      applyDefaultRegionsExpandedState() {
        if (this.hasAppliedDefaultRegionsState) {
          return;
        }

        this.setAllRegionsExpanded(Boolean(this.defaultRegionsExpanded));
        this.hasAppliedDefaultRegionsState = true;
      },
      getDisplaySignature(section, sectionId) {
        return [
          sectionId,
          Boolean(section.isLiked),
          Boolean(section.video_info),
          Boolean(section.topo_info),
          sectionHasCamera(section),
          Boolean(section.direction_info),
          section.level_status || "",
          section.current_level,
          section.current_trend,
          section.level_last_update || "",
          Boolean(section.free_access),
        ].join("|");
      },
      toDisplaySection(section, sectionId) {
        const normalizedSection = {
          ...section,
          id: sectionId,
          isLiked: Boolean(section.isLiked),
        };

        const trend = Number(normalizedSection.current_trend);
        const currentLevel = normalizedSection.current_level;
        const hasTrend = !isNaN(trend) && !(trend === 0 && currentLevel == 0);

        return {
          ...normalizedSection,
          _videoIconColor: normalizedSection.video_info ? "blue" : "icon-inactive",
          _topoIconColor: normalizedSection.topo_info ? "blue" : "icon-inactive",
          _directionIconColor: normalizedSection.direction_info ? "blue" : "icon-inactive",
          _levelColorClass: this.getColor(normalizedSection),
          _borderClass: this.getBorderColor(normalizedSection),
          _formattedCurrentLevel:
            normalizedSection.current_level === "NaN" || normalizedSection.current_level == 0
              ? ""
              : this.formatCurrentLevel(normalizedSection.current_level),
          _currentTrendDisplay: hasTrend ? Math.abs(trend) : "",
          _currentTrendSign: hasTrend ? Math.sign(trend) : 0,
          _lastUpdate: RiversService.getLastUpdate(normalizedSection.level_last_update),
          _lastUpdateDayMonth: StringService.getLastUpdateDayAndMonth(normalizedSection.level_last_update),
          _displaySignature: this.getDisplaySignature(normalizedSection, sectionId),
        };
      },
      getHeaders() {
        return this.$vuetify.display.name === "xs" ? this.headersMobile : this.headers;
      },
      getColor(obj) {
        return levelTextClass(obj.level_status);
      },
      getBorderColor(obj) {
        const padding = " pl-2 pr-2";
        if (this.isMember || obj.free_access) {
          if (obj.level_status === this.level_recommended) {
            return "border-recommended" + padding;
          }
          if (obj.level_status === this.level_high) {
            return "border-too-high" + padding;
          }
          if (obj.level_status === this.level_low) {
            return "border-too-low" + padding;
          }
          if (obj.level_status === this.level_unknown) {
            return "border-unknown" + padding;
          }
        }
        return "border-unknown" + padding;
      },
      formatCurrentLevel(value) {
        // Check if value is undefined, null, or "NaN"
        if (value === undefined || value === null || value === "NaN") {
          return "";
        }

        // Parse the value to a number
        const numValue = parseFloat(value);

        // Check if the value is NaN
        if (isNaN(numValue)) {
          return "";
        }

        // For values 1000 or higher, display without decimals
        if (numValue >= 1000) {
          return Math.round(numValue).toString();
        }

        // If it ends with .00, remove the decimal part
        if (value.toString().endsWith(".00")) {
          return Math.floor(numValue).toString();
        }

        return value.toString();
      },
      showRiver(row) {
        var slug = row.alias;
        this.isMember || row.free_access === true
          ? this.$router.push({ name: "riviere", params: { slug: slug } })
          : "";
      },
      async toggleLike(item) {
        if (!this.authUser || !this.authUser.uid) {
          console.error("User is not logged in or user data is missing.");
          return;
        }

        const uid = this.authUser.uid;
        const sectionId = item.id || item.sectionId;

        if (!sectionId) {
          console.error("Missing section id for like/unlike action.");
          return;
        }

        if (item.isLiked) {
          await UserService.actions.unlikeSection(uid, sectionId);
          item.isLiked = false;
          this.$emit("liked-changed", { sectionId, isLiked: false });
        } else {
          await UserService.actions.likeSection(uid, sectionId);
          item.isLiked = true;
          this.$emit("liked-changed", { sectionId, isLiked: true });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  header {
    display: flex;
    align-items: center;
  }

  .class-text {
    font-size: 0.85rem;
  }

  .flow-column {
    align-content: right;
  }

  .number-rapids-text {
    font-size: 0.75rem;
  }

  .rapids-class-text {
    font-size: 0.68rem;
  }

  .number-rapids-width {
    width: 70px;
  }

  .current-trend-text {
    font-size: 0.75rem;
  }

  .last-update-text {
    font-size: 0.6rem;
  }

  .margin-top {
    margin-top: 2rem;
  }

  .collapsed > .when-open,
  .not-collapsed > .when-closed {
    display: none;
  }

  .bold {
    font-weight: bold;
  }

  /* Trend drawn behind the section cell (right part), under the text */
  .region-header td {
    background: rgba(var(--v-theme-primary), 0.06);
    cursor: pointer;
  }
  .region-header__name {
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
  }
  .region-header__count {
    font-size: 0.8rem;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }
  .row--no-data td {
    padding-top: 2px !important;
    padding-bottom: 2px !important;
    opacity: 0.75;
  }

  .flow-header {
    white-space: nowrap;
  }

  .border-too-high {
    border-left: 4px solid rgb(var(--v-theme-level-high));
  }

  .border-recommended {
    border-left: 4px solid rgb(var(--v-theme-level-good));
  }

  .border-too-low {
    border-left: 4px solid rgb(var(--v-theme-level-low));
  }

  .border-unknown {
    border-left: 4px solid lightgrey;
  }

  #blur {
    filter: blur(3px);
    -webkit-filter: blur(3px);
  }

  /* Members-only flow: a pale gold badge, like the brand's special badges (docs/BRAND.md) */
  .premium-hint {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin: 2px 0;
    padding: 1px 8px 1px 6px;
    border-radius: 999px;
    border: 1px solid rgba(var(--v-theme-gold), 0.5);
    background: rgb(var(--v-theme-gold-tint));
    color: rgb(var(--v-theme-on-gold-tint));
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    line-height: 1.5;
  }
</style>
